/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Properties Inspector Panel
   Dynamic parameter controls generated from ParamDefinition.
   Integrates continuous scrubbing, numeric companion inputs,
   parameter reset affordance, and node bypass controls.
   ═══════════════════════════════════════════════════════════════ */

import React, { useRef } from 'react';
import { Sliders, Upload, Eye, EyeOff, RotateCcw } from 'lucide-react';
import { defaultNodeRegistry } from '../../nodes';
import { SetParameterCommand, BypassNodeCommand, ResetParamCommand } from '../../domain/commands';
import { generateStableId } from '../../domain/id';
import { useDomainBridge } from '../../stores/domainBridge';
import { useSelectionStore } from '../../stores/selectionStore';
import { useTransientStore } from '../../stores/transientStore';
import styles from './Inspector.module.css';

export const Inspector: React.FC = () => {
  const { domainState, dispatch } = useDomainBridge();
  const { selectedNodeIds } = useSelectionStore();
  const { setMonitoredPreviewNodeId, monitoredPreviewNodeId } = useTransientStore();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrubSessionIdRef = useRef<string | undefined>(undefined);

  // Take the first selected node
  const activeNodeId = Array.from(selectedNodeIds)[0];
  const node = activeNodeId ? domainState.canonical.nodes[activeNodeId] : null;
  const def = node ? defaultNodeRegistry.get(node.type) : null;

  if (!node || !def) {
    return (
      <aside className={`${styles.inspectorPanel} ${styles.emptyInspectorPanel}`}>
        <div className={styles.emptyState}>
          <Sliders size={24} opacity={0.4} />
          <div>Select a node to inspect and tune its parameters</div>
        </div>
      </aside>
    );
  }

  const handleParamChange = (
    paramId: string,
    newValue: unknown,
    isMergeable = true,
    sessionId?: string
  ) => {
    const oldValue = node.params[paramId];
    dispatch(new SetParameterCommand(node.id, paramId, oldValue, newValue, isMergeable, sessionId));
  };

  const handleResetParam = (paramId: string, defaultValue: unknown) => {
    const oldValue = node.params[paramId];
    dispatch(new ResetParamCommand(node.id, paramId, oldValue, defaultValue));
  };

  const handleToggleBypass = () => {
    dispatch(new BypassNodeCommand(node.id));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      if (dataUrl) {
        handleParamChange('sourceType', 'file', false);
        handleParamChange('imageDataUrl', dataUrl, false);
      }
    };
    reader.readAsDataURL(file);
  };

  const isMonitored = monitoredPreviewNodeId === node.id;
  const isBypassed = Boolean(node.bypassed);

  return (
    <aside className={styles.inspectorPanel}>
      {/* Node Info Header */}
      <div className={styles.panelHeader}>
        <div className={styles.headerTop}>
          <span className={styles.nodeTypeTitle}>{node.meta?.label || def.label}</span>
          <span className={styles.nodeCategoryBadge}>{def.category}</span>
        </div>
        <div className={styles.nodeDesc}>{def.description}</div>

        <div className={styles.headerActions}>
          <button
            className={styles.actionButton}
            style={{
              borderColor: isMonitored ? 'var(--accent)' : undefined,
              color: isMonitored ? 'var(--accent)' : undefined,
            }}
            onClick={() => setMonitoredPreviewNodeId(isMonitored ? null : node.id)}
            title="Monitor stream in preview panel"
          >
            <Eye size={13} />
            <span>{isMonitored ? 'Monitored' : 'Monitor'}</span>
          </button>

          <button
            className={styles.actionButton}
            style={{
              borderColor: isBypassed ? '#f59e0b' : undefined,
              color: isBypassed ? '#f59e0b' : undefined,
            }}
            onClick={handleToggleBypass}
            title="Bypass node execution and pass input through"
          >
            <EyeOff size={13} />
            <span>{isBypassed ? 'Bypassed' : 'Bypass'}</span>
          </button>
        </div>
      </div>

      <div className={styles.scrollArea}>
        {/* Dynamic Parameters driven by ParamDefinition */}
        {def.params.length > 0 && (
          <div>
            <div className={styles.sectionTitle}>Parameters</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {def.params.map((param) => {
                const currentValue = node.params[param.id] ?? param.defaultValue;
                const isModified = currentValue !== param.defaultValue;

                return (
                  <div key={param.id} className={styles.paramGroup}>
                    <div className={styles.paramHeader}>
                      <div className={styles.paramHeaderLeft}>
                        <label className={styles.paramLabel}>{param.name}</label>
                        {isModified && (
                          <button
                            className={styles.resetButton}
                            onClick={() => handleResetParam(param.id, param.defaultValue)}
                            title="Reset to default value"
                          >
                            <RotateCcw size={10} />
                          </button>
                        )}
                      </div>
                      <span className={styles.paramValueBadge}>
                        {typeof currentValue === 'number'
                          ? Number(currentValue).toFixed(param.precision ?? (param.step && param.step < 1 ? 2 : 0))
                          : String(currentValue)}
                        {param.unit ? ` ${param.unit}` : ''}
                      </span>
                    </div>

                    {/* Slider Widget with companion numeric input */}
                    {(param.type === 'slider' || param.type === 'angle') && (
                      <div className={styles.sliderRow}>
                        <input
                          type="range"
                          className={styles.sliderInput}
                          min={param.min ?? 0}
                          max={param.max ?? 100}
                          step={param.step ?? (param.type === 'angle' ? 1 : 0.05)}
                          value={Number(currentValue)}
                          onPointerDown={() => {
                            scrubSessionIdRef.current = generateStableId('cmd');
                          }}
                          onChange={(e) =>
                            handleParamChange(
                              param.id,
                              parseFloat(e.target.value),
                              true,
                              scrubSessionIdRef.current
                            )
                          }
                          onPointerUp={() => {
                            scrubSessionIdRef.current = undefined;
                          }}
                        />
                        <input
                          type="number"
                          className={styles.sliderNumber}
                          min={param.min}
                          max={param.max}
                          step={param.step ?? 1}
                          value={Number(currentValue)}
                          onChange={(e) =>
                            handleParamChange(param.id, parseFloat(e.target.value) || 0, false)
                          }
                        />
                      </div>
                    )}

                    {/* Color Widget */}
                    {param.type === 'color' && (
                      <div className={styles.colorPickerRow}>
                        <input
                          type="color"
                          className={styles.colorInput}
                          value={String(currentValue)}
                          onChange={(e) => handleParamChange(param.id, e.target.value, false)}
                        />
                        <input
                          type="text"
                          className={styles.textInput}
                          value={String(currentValue)}
                          onChange={(e) => handleParamChange(param.id, e.target.value, false)}
                        />
                      </div>
                    )}

                    {/* Number Widget */}
                    {param.type === 'number' && (
                      <input
                        type="number"
                        className={styles.numberInput}
                        min={param.min}
                        max={param.max}
                        step={param.step ?? 1}
                        value={Number(currentValue)}
                        onChange={(e) =>
                          handleParamChange(param.id, parseFloat(e.target.value) || 0, false)
                        }
                      />
                    )}

                    {/* Select Widget */}
                    {param.type === 'select' && param.options && (
                      <select
                        className={styles.selectInput}
                        value={String(currentValue)}
                        onChange={(e) => handleParamChange(param.id, e.target.value, false)}
                      >
                        {param.options.map((opt) => (
                          <option key={String(opt.value)} value={String(opt.value)}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    )}

                    {/* Boolean Toggle Widget */}
                    {param.type === 'boolean' && (
                      <label className={styles.toggleRow}>
                        <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                          {currentValue ? 'Enabled' : 'Disabled'}
                        </span>
                        <input
                          type="checkbox"
                          checked={Boolean(currentValue)}
                          onChange={(e) => handleParamChange(param.id, e.target.checked, false)}
                        />
                      </label>
                    )}

                    {/* Text Widget */}
                    {param.type === 'text' && (
                      <input
                        type="text"
                        className={styles.textInput}
                        value={String(currentValue)}
                        onChange={(e) => handleParamChange(param.id, e.target.value, false)}
                      />
                    )}
                  </div>
                );
              })}

              {/* File Upload for Image Source */}
              {node.type === 'input.image' && (
                <div style={{ marginTop: '8px' }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                  />
                  <button
                    className={styles.fileButton}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Upload size={14} />
                    <span>Upload Local Image...</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Port Status */}
        <div>
          <div className={styles.sectionTitle}>Ports</div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              fontSize: '11px',
              color: 'var(--text-secondary)',
            }}
          >
            <div>
              Inputs: {Object.keys(node.inputs).length} (
              {Object.values(node.inputs).map((p) => p.name).join(', ') || 'None'})
            </div>
            <div>
              Outputs: {Object.keys(node.outputs).length} (
              {Object.values(node.outputs).map((p) => p.name).join(', ') || 'None'})
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};
